// src/features/map/vehicleGrouping.js
//
// Multi-tracker vehicles — the SINGLE pure source of truth that both the Live
// Map list (VehicleListPanel) and the map markers (MapView.buildGeoJSON) use to
// collapse the 2-4 GPS trackers inside one physical car into ONE row / ONE
// marker. Keeping the grouping here (not duplicated in each consumer) guarantees
// the list and the map always agree on which trackers belong together and where
// the car "is".
//
// A device carries `vehicleId` + `isPrimary` (from GET /api/devices — see
// migration 065). Unlinked devices (vehicleId == null) are untouched and stay
// first-class in both surfaces.
import { isPositionStale } from '@/lib/positionFreshness';

/** True when the device is the primary tracker of a vehicle (its fix is the
 *  car's canonical position). */
export function isPrimaryTracker(device) {
  return !!device?.vehicleId && device.isPrimary === true;
}

/** True when the device is a linked-but-secondary tracker (folded out of the
 *  top-level list; still fully tracked + evaluated server-side). */
export function isSecondaryTracker(device) {
  return !!device?.vehicleId && device.isPrimary !== true;
}

/**
 * Pick the member tracker whose fix best represents the whole car.
 *
 * BUSINESS RULE (the anti-theft payoff): prefer the primary tracker's fix while
 * it is still fresh, but if the primary has gone stale/silent — e.g. a thief
 * found and pulled it — fall back to the MOST RECENT fix among the remaining
 * trackers so the marker keeps following the real car instead of freezing on the
 * primary's last known spot. Freshness uses the same `staleMinutes` threshold as
 * the rest of the app so the rollup can't disagree with per-device status.
 *
 * Returning the owning device (not just the position) matters for status colour:
 * classifying the car from the BEST tracker's device sidesteps the primary
 * device's own `status === 'offline'` short-circuit, so a car running on a live
 * sibling shows Moving instead of the pulled-primary's Offline red.
 *
 * @returns {{ device: object|null, position: object|null }}
 */
export function pickBestMember(members, positionsByDeviceId, thresholds) {
  const staleMs = (thresholds?.staleMinutes ?? 10) * 60_000;
  const primary = members.find((m) => m.isPrimary) ?? members[0] ?? null;
  const primaryPos = primary ? positionsByDeviceId[primary.id] : null;

  // Fresh primary → that's the car.
  if (primaryPos && !isPositionStale(primaryPos, staleMs)) {
    return { device: primary, position: primaryPos };
  }

  // Primary stale/missing → freshest live sibling (else the stale primary fix).
  let bestDevice = primary;
  let bestPos = primaryPos ?? null;
  let bestMs = bestPos?.fixTime ? new Date(bestPos.fixTime).getTime() : -Infinity;
  for (const m of members) {
    const p = positionsByDeviceId[m.id];
    if (!p?.fixTime) continue;
    const ms = new Date(p.fixTime).getTime();
    if (ms > bestMs) {
      bestDevice = m;
      bestPos = p;
      bestMs = ms;
    }
  }
  return { device: bestDevice, position: bestPos };
}

/** Back-compat convenience: the best position only. */
export function bestVehiclePosition(members, positionsByDeviceId, thresholds) {
  return pickBestMember(members, positionsByDeviceId, thresholds).position;
}

/**
 * Detect tracker DIVERGENCE — the tamper/removal fingerprint that drives the
 * "suspect" red pulse on the map + the warning badge in the list.
 *
 * A car with 2+ trackers is suspicious when at least one tracker is still LIVE
 * (fresh fix) while at least one other tracker that WAS reporting has gone
 * SILENT (its last fix is now stale). That disagreement is the signal: a live
 * sibling proves the car still has power + cell coverage, so the silent tracker
 * didn't merely lose signal — it was likely removed.
 *
 * Deliberately NOT suspicious:
 *  - a single-tracker car (no redundancy to disagree),
 *  - ALL trackers silent together (underground parking / lost coverage),
 *  - a tracker that has NEVER reported (newly added, not "gone" silent).
 *
 * @returns {{ suspect: boolean, silentMemberIds: number[] }}
 */
export function detectDivergence(members, positionsByDeviceId, thresholds) {
  const staleMs = (thresholds?.staleMinutes ?? 10) * 60_000;
  if (!members || members.length < 2) return { suspect: false, silentMemberIds: [] };

  let liveCount = 0;
  const silentMemberIds = [];
  for (const m of members) {
    const p = positionsByDeviceId[m.id];
    if (!p?.fixTime) continue; // never reported → unknown, not "went silent"
    if (isPositionStale(p, staleMs)) silentMemberIds.push(m.id);
    else liveCount += 1;
  }

  const suspect = liveCount >= 1 && silentMemberIds.length >= 1;
  return { suspect, silentMemberIds: suspect ? silentMemberIds : [] };
}

/**
 * Group a device list into vehicles.
 *
 * @param devices  array of device objects (each may carry vehicleId/isPrimary)
 * @param positionsByDeviceId  Redux `session.positions`
 * @param thresholds  useStatusThresholds() output
 * @returns {{
 *   membersByVehicle: Map<string, object[]>,   // trackers per vehicle, primary first
 *   bestPositionByVehicle: Map<string, object|null>,
 *   bestDeviceIdByVehicle: Map<string, number|null>,   // owner of the best fix (for status colour)
 *   primaryDeviceIdByVehicle: Map<string, number|null>,
 *   suspectByVehicle: Map<string, boolean>,            // tamper-divergence detected
 *   silentMemberIdsByVehicle: Map<string, number[]>,   // which trackers went silent (auto-expand)
 *   linkedDeviceIds: Set<number>,              // every device that belongs to a vehicle
 *   secondaryDeviceIds: Set<number>,           // linked but NOT primary (map/list fold these out)
 * }}
 */
export function groupDevicesIntoVehicles(devices, positionsByDeviceId = {}, thresholds) {
  const membersByVehicle = new Map();
  const linkedDeviceIds = new Set();
  const secondaryDeviceIds = new Set();

  for (const device of devices || []) {
    if (!device?.vehicleId) continue;
    linkedDeviceIds.add(device.id);
    if (device.isPrimary !== true) secondaryDeviceIds.add(device.id);
    const list = membersByVehicle.get(device.vehicleId) ?? [];
    list.push(device);
    membersByVehicle.set(device.vehicleId, list);
  }

  const bestPositionByVehicle = new Map();
  const bestDeviceIdByVehicle = new Map();
  const primaryDeviceIdByVehicle = new Map();
  const suspectByVehicle = new Map();
  const silentMemberIdsByVehicle = new Map();
  for (const [vehicleId, members] of membersByVehicle) {
    // Primary first, then by device id — stable ordering for the expanded list.
    members.sort((a, b) => {
      if (!!a.isPrimary !== !!b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.id - b.id;
    });
    const primary = members.find((m) => m.isPrimary) ?? null;
    primaryDeviceIdByVehicle.set(vehicleId, primary ? primary.id : null);

    const best = pickBestMember(members, positionsByDeviceId, thresholds);
    bestPositionByVehicle.set(vehicleId, best.position);
    bestDeviceIdByVehicle.set(vehicleId, best.device ? best.device.id : null);

    const divergence = detectDivergence(members, positionsByDeviceId, thresholds);
    suspectByVehicle.set(vehicleId, divergence.suspect);
    silentMemberIdsByVehicle.set(vehicleId, divergence.silentMemberIds);
  }

  return {
    membersByVehicle,
    bestPositionByVehicle,
    bestDeviceIdByVehicle,
    primaryDeviceIdByVehicle,
    suspectByVehicle,
    silentMemberIdsByVehicle,
    linkedDeviceIds,
    secondaryDeviceIds,
  };
}
